import { CallParticipant, TrackType } from 'src/app/common/interfaces/rtc-interface';
import { NetworkQuality } from 'src/app/models/network';
import { User } from 'src/app/models/user';
import { VideoTrackType } from 'src/app/services/session-call-tracks.service';
import { BehaviorSubject } from 'rxjs';
import { ITileData } from 'src/app/services/session-shared-data.service';

export interface UserTile {
  user$: BehaviorSubject<User>;
  participant$: BehaviorSubject<CallParticipant>;
  track$: BehaviorSubject<VideoTrackType>;
  networkQuality$: BehaviorSubject<NetworkQuality>;
  visible$: BehaviorSubject<boolean>;
  active$: BehaviorSubject<boolean>;
  emojiId$: BehaviorSubject<number>;
  top$: BehaviorSubject<number | undefined>;
  left$: BehaviorSubject<number | undefined>;
  width$: BehaviorSubject<number | undefined>;
  height$: BehaviorSubject<number | undefined>;
  // Indicates the order of visible tiles
  visibilityIndex$: BehaviorSubject<number | undefined>;
  pipVideo$: BehaviorSubject<boolean>;
}

export function createDefaultUserTile(
  user: User,
  callParticipant: CallParticipant,
  trackType: TrackType,
  state: boolean,
): UserTile {
  return {
    user$: new BehaviorSubject(user),
    participant$: new BehaviorSubject(callParticipant),
    track$: new BehaviorSubject<VideoTrackType>(trackType as VideoTrackType),
    networkQuality$: new BehaviorSubject<NetworkQuality>(NetworkQuality.UNKNOWN),
    visible$: new BehaviorSubject<boolean>(false),
    active$: new BehaviorSubject<boolean>(state),
    emojiId$: new BehaviorSubject(-1),
    top$: new BehaviorSubject<number | undefined>(undefined),
    left$: new BehaviorSubject<number | undefined>(undefined),
    width$: new BehaviorSubject<number | undefined>(undefined),
    height$: new BehaviorSubject<number | undefined>(undefined),
    visibilityIndex$: new BehaviorSubject<number | undefined>(undefined),
    pipVideo$: new BehaviorSubject<boolean>(false),
  };
}

/**
 * Creates a user object from a participant. Used when an AI agent is joining the call.
 */
export function createDummyUser(participant: CallParticipant): User {
  return {
    _id: participant.userId,
    name: participant.name ?? participant.agentInfo?.name ?? '',
    profile_image: '',
    email: '',
    courses: [],
    qualifications: [],
    org_name: '',
    role: 'TEACHER',
    lastLogin: '',
    location: '',
    createdAt: '',
    createdDate: '',
    updatedAt: '',
    generatedApiKey: false,
    apiKeyGeneratedOn: new Date(),
    defaultSpaceCreated: false,
    timezone: { autoDetect: false, value: '' },
    enabled_capabilities: [],
    institution: {
      _id: '',
      description: '',
      email: '',
      name: '',
      phone: '',
      logo: '',
      location: [],
      personas: {},
    },
  };
}

function getTileIdentity(tile: UserTile | ITileData): { userId: string; trackType: string } {
  if ('user$' in tile) {
    return {
      userId: tile.user$.getValue()._id,
      trackType: tile.track$.getValue() as string,
    };
  }
  if ('userId' in tile) {
    return { userId: tile.userId, trackType: tile.trackType };
  }

  console.error('Invalid tile type', tile);
  return { userId: '', trackType: '' };
}

export function isSameTile(tile1: UserTile | ITileData, tile2: UserTile | ITileData): boolean {
  const id1 = getTileIdentity(tile1);
  const id2 = getTileIdentity(tile2);
  return id1.userId === id2.userId && id1.trackType === id2.trackType;
}
